import { SOCIAL_PLATFORM_META, type SocialLinksInput, type SocialPlatform } from '../../shared/social-links'

export type SocialLinksFormState = Record<SocialPlatform, string>

export function emptySocialLinksForm(): SocialLinksFormState {
  return Object.fromEntries(
    Object.keys(SOCIAL_PLATFORM_META).map((k) => [k, '']),
  ) as SocialLinksFormState
}

export function socialLinksFormFromApi(links: Array<{ platform: SocialPlatform; handle: string }>): SocialLinksFormState {
  const form = emptySocialLinksForm()
  for (const link of links) form[link.platform] = link.handle
  return form
}

export function socialLinksInputFromForm(form: SocialLinksFormState): SocialLinksInput {
  const out: SocialLinksInput = {}
  for (const [platform, handle] of Object.entries(form)) {
    const trimmed = handle.trim()
    out[platform as SocialPlatform] = trimmed || null
  }
  return out
}

export function SocialLinksEditor({
  value,
  onChange,
  compact,
}: {
  value: SocialLinksFormState
  onChange: (next: SocialLinksFormState) => void
  compact?: boolean
}) {
  const platforms = (Object.keys(SOCIAL_PLATFORM_META) as SocialPlatform[]).slice(0, compact ? 6 : undefined)

  return (
    <div className={`social-links-editor${compact ? ' social-links-editor--compact' : ''}`}>
      <p className="field-hint">
        Add usernames only — Instagram, TikTok, Snapchat, and other approved platforms. No phone numbers or random websites in your bio.
      </p>
      <div className="social-links-grid">
        {platforms.map((platform) => {
          const meta = SOCIAL_PLATFORM_META[platform]
          return (
            <label key={platform} className="social-link-field">
              <span>{meta.label}</span>
              <input
                className="input"
                placeholder={meta.placeholder}
                value={value[platform]}
                onChange={(e) => onChange({ ...value, [platform]: e.target.value })}
                autoComplete="off"
                spellCheck={false}
              />
            </label>
          )
        })}
      </div>
      {!compact && (
        <details className="social-links-more">
          <summary>More platforms (YouTube, Twitch, creator links…)</summary>
          <div className="social-links-grid">
            {(Object.keys(SOCIAL_PLATFORM_META) as SocialPlatform[])
              .slice(6)
              .map((platform) => {
                const meta = SOCIAL_PLATFORM_META[platform]
                return (
                  <label key={platform} className="social-link-field">
                    <span>{meta.label}</span>
                    <input
                      className="input"
                      placeholder={meta.placeholder}
                      value={value[platform]}
                      onChange={(e) => onChange({ ...value, [platform]: e.target.value })}
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </label>
                )
              })}
          </div>
        </details>
      )}
    </div>
  )
}

export function SocialLinksRow({ links }: { links: Array<{ platform: SocialPlatform; handle: string; url: string; label: string }> }) {
  if (!links.length) return null
  return (
    <div className="social-links-row">
      {links.map((link) => (
        <a
          key={link.platform}
          className="social-link-chip"
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${link.label}: ${link.handle}`}
        >
          <span className="social-link-chip-label">{link.label}</span>
          <span className="social-link-chip-handle">@{link.handle.replace(/^@/, '')}</span>
        </a>
      ))}
    </div>
  )
}
